import React, { useEffect, useMemo, useState } from 'react';
import { Check, Minus } from 'lucide-react';

export function useTableSelection(rows, getRowId = (row) => row.id) {
  const visibleIds = useMemo(
    () => rows.map((row) => String(getRowId(row))).filter(Boolean),
    [rows, getRowId]
  );
  const visibleKey = visibleIds.join('|');
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  useEffect(() => {
    setSelectedIds(new Set());
  }, [visibleKey]);

  const selectedCount = selectedIds.size;
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  const toggleRow = (id) => {
    const key = String(id);
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds((current) => {
      if (visibleIds.length > 0 && visibleIds.every((id) => current.has(id))) {
        return new Set();
      }
      return new Set(visibleIds);
    });
  };

  return {
    allVisibleSelected,
    clearSelection: () => setSelectedIds(new Set()),
    isSelected: (id) => selectedIds.has(String(id)),
    selectedCount,
    someVisibleSelected,
    toggleAllVisible,
    toggleRow,
  };
}

export function SelectionCheckbox({ checked, indeterminate = false, onCheckedChange, ariaLabel }) {
  const active = checked || indeterminate;

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      aria-label={ariaLabel}
      onClick={(event) => {
        event.stopPropagation();
        onCheckedChange?.(event);
      }}
      className={`mx-auto inline-flex h-4 w-4 items-center justify-center rounded-[2px] border transition-colors ${active ? 'border-zinc-900 bg-zinc-900 text-white' : 'border-zinc-300 bg-white text-transparent hover:border-zinc-400'}`}
    >
      {indeterminate ? (
        <Minus className="h-3 w-3" strokeWidth={2.2}/>
      ) : (
        <Check className="h-3 w-3" strokeWidth={2.2}/>
      )}
    </button>
  );
}

export function SelectionActionBar({ count, onClear }) {
  if (!count) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border border-zinc-200 bg-white px-3 py-2 text-sm">
      <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
        {count} selected
      </span>
      <button
        type="button"
        onClick={onClear}
        className="text-xs font-medium text-zinc-500 hover:text-zinc-900"
      >
        Clear selection
      </button>
    </div>
  );
}
